import {
    AlertTriangle,
    ChevronDown,
    ChevronRight,
    MoreHorizontal,
} from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

import {
    getAvailabilityType,
    type ScheduleDayGroup,
    type SourceLabel,
    type TimeboxedAvailabilityEntry,
    type TimeboxedAvailabilityEntrySegment,
    type TimeboxedAvailabilitySegment,
    type TimeboxedScheduleSegment,
    type TimelineItemType,
    type WeeklyAvailabilityEntry,
    type WeeklyAvailabilityEntrySegment,
    type WeeklyScheduleRow,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_model';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { cn } from '@apps/web/lib/utils';

const SCHEDULE_ROW_GRID_CLASS =
    'grid grid-cols-[0.75rem_5rem_minmax(6rem,0.95fr)_minmax(0,1fr)_1.25rem] items-center gap-x-1.5 gap-y-1';
const SCHEDULE_ROW_PADDING_CLASS = 'py-1.5 pr-1.5 pl-0.5';
const SCHEDULE_DETAIL_CONTAINER_CLASS = 'bg-muted/20 py-1.5';

type BadgeVariant = 'success' | 'destructive' | 'secondary';

const TYPE_BADGE_CONFIG: Record<
    TimelineItemType,
    { label: string; variant: BadgeVariant }
> = {
    availability: {
        label: 'Available',
        variant: 'success',
    },
    unavailability: {
        label: 'Unavailable',
        variant: 'destructive',
    },
    shift: {
        label: 'Shift',
        variant: 'secondary',
    },
};

export function TimeboxedScheduleList({
    groups,
    expandedRows,
    toggleExpandedRow,
    onEditEntry,
    onDeleteEntry,
}: {
    readonly groups: ReadonlyArray<ScheduleDayGroup<TimeboxedScheduleSegment>>;
    readonly expandedRows: ReadonlySet<string>;
    readonly toggleExpandedRow: (rowId: string) => void;
    readonly onEditEntry: (entry: TimeboxedAvailabilityEntry) => void;
    readonly onDeleteEntry: (entry: TimeboxedAvailabilityEntry) => void;
}) {
    return (
        <div className="overflow-hidden rounded-md border">
            {groups.map((group, groupIndex) => (
                <div
                    key={group.key}
                    className={cn(groupIndex > 0 ? 'border-t' : '')}
                >
                    <div className="bg-muted/25 px-2 py-1.5 text-sm font-semibold">
                        {group.label}
                    </div>
                    <div className="divide-y">
                        {group.items.map((item) =>
                            item.kind === 'shift' ? (
                                <ShiftScheduleRow key={item.id} item={item} />
                            ) : (
                                <TimeboxedScheduleRow
                                    key={item.id}
                                    item={item}
                                    isExpanded={expandedRows.has(item.id)}
                                    onToggleExpanded={() =>
                                        toggleExpandedRow(item.id)
                                    }
                                    onEditEntry={onEditEntry}
                                    onDeleteEntry={onDeleteEntry}
                                />
                            ),
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

export function WeeklyScheduleList({
    groups,
    expandedRows,
    toggleExpandedRow,
    onEditEntry,
    onDeleteEntry,
}: {
    readonly groups: ReadonlyArray<ScheduleDayGroup<WeeklyScheduleRow>>;
    readonly expandedRows: ReadonlySet<string>;
    readonly toggleExpandedRow: (rowId: string) => void;
    readonly onEditEntry: (entry: WeeklyAvailabilityEntry) => void;
    readonly onDeleteEntry: (entry: WeeklyAvailabilityEntry) => void;
}) {
    return (
        <div className="overflow-hidden rounded-md border">
            {groups.map((group, groupIndex) => (
                <div
                    key={group.key}
                    className={cn(groupIndex > 0 ? 'border-t' : '')}
                >
                    <div className="bg-muted/25 px-2 py-1.5 text-sm font-semibold">
                        {group.label}
                    </div>
                    <div className="divide-y">
                        {group.items.map((item) => (
                            <WeeklyScheduleListRow
                                key={item.id}
                                item={item}
                                isExpanded={expandedRows.has(item.id)}
                                onToggleExpanded={() =>
                                    toggleExpandedRow(item.id)
                                }
                                onEditEntry={onEditEntry}
                                onDeleteEntry={onDeleteEntry}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function getTypeBadgeConfig(type: TimelineItemType) {
    return TYPE_BADGE_CONFIG[type];
}

function AvailabilityActionMenu({
    onEdit,
    onDelete,
}: {
    readonly onEdit: () => void;
    readonly onDelete: () => void;
}) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 shrink-0"
                >
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit}>Edit</DropdownMenuItem>
                <DropdownMenuItem variant="destructive" onClick={onDelete}>
                    Delete
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function ScheduleActionCell({ children }: { readonly children?: ReactNode }) {
    return (
        <div className="flex h-5 items-center justify-end">
            {children ?? <span aria-hidden={true} className="h-5 w-5" />}
        </div>
    );
}

function getScheduleRowClass(hasConflict: boolean) {
    return cn(
        SCHEDULE_ROW_GRID_CLASS,
        'border-l-2',
        hasConflict
            ? 'border-l-amber-500 bg-amber-50/60 dark:bg-amber-950/20'
            : 'border-l-transparent',
    );
}

function ConflictBadge() {
    return (
        <Badge
            variant="outline"
            className="border-amber-300 bg-amber-50 px-1 py-0 text-[10px] leading-4 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300"
            title="Sources disagree on this availability window."
        >
            <AlertTriangle className="mr-0.5 h-3 w-3" />
            Conflict
        </Badge>
    );
}

function SourceLabelText({
    sourceLabel,
}: {
    readonly sourceLabel: SourceLabel;
}) {
    return sourceLabel ? (
        <span
            className="text-muted-foreground block max-w-full min-w-0 text-right text-xs leading-4 break-normal whitespace-normal"
            title={sourceLabel}
        >
            {sourceLabel}
        </span>
    ) : (
        <span className="text-muted-foreground text-right text-xs leading-4">
            --
        </span>
    );
}

function ScheduleSourceCell({
    sourceLabel,
    hasConflict = false,
}: {
    readonly sourceLabel: SourceLabel;
    readonly hasConflict?: boolean;
}) {
    return (
        <div className="flex min-w-0 flex-col items-end justify-center gap-0.5 pl-3">
            {hasConflict ? <ConflictBadge /> : null}
            <SourceLabelText sourceLabel={sourceLabel} />
        </div>
    );
}

function StatusBadge({ type }: { readonly type: TimelineItemType }) {
    const badge = getTypeBadgeConfig(type);

    return (
        <Badge
            variant={badge.variant}
            className="px-1.5 py-0 text-[11px] leading-4"
        >
            {badge.label}
        </Badge>
    );
}

function EntriesDisclosureButton({
    isExpanded,
    onToggleExpanded,
}: {
    readonly isExpanded: boolean;
    readonly onToggleExpanded: () => void;
}) {
    return (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-4 w-4"
            aria-label={
                isExpanded
                    ? 'Collapse availability details'
                    : 'Expand availability details'
            }
            onClick={onToggleExpanded}
        >
            {isExpanded ? (
                <ChevronDown className="h-3 w-3" />
            ) : (
                <ChevronRight className="h-3 w-3" />
            )}
        </Button>
    );
}

function TimeboxedEntryDetails({
    entrySegments,
    onEditEntry,
    onDeleteEntry,
}: {
    readonly entrySegments: ReadonlyArray<TimeboxedAvailabilityEntrySegment>;
    readonly onEditEntry: (entry: TimeboxedAvailabilityEntry) => void;
    readonly onDeleteEntry: (entry: TimeboxedAvailabilityEntry) => void;
}) {
    return (
        <div className={SCHEDULE_DETAIL_CONTAINER_CLASS}>
            <div className="space-y-1">
                {entrySegments.map((segment) => {
                    return (
                        <div
                            key={segment.id}
                            className={cn(
                                getScheduleRowClass(false),
                                SCHEDULE_ROW_PADDING_CLASS,
                                'min-h-6 text-xs',
                            )}
                        >
                            <div />
                            <div className="flex justify-start">
                                <StatusBadge
                                    type={getAvailabilityType(segment.status)}
                                />
                            </div>
                            <div className="text-foreground whitespace-nowrap tabular-nums">
                                {segment.timeLabel}
                            </div>
                            <ScheduleSourceCell
                                sourceLabel={segment.sourceLabel}
                            />
                            <ScheduleActionCell>
                                {segment.source === 'manual' ? (
                                    <AvailabilityActionMenu
                                        onEdit={() =>
                                            onEditEntry(segment.entry)
                                        }
                                        onDelete={() =>
                                            onDeleteEntry(segment.entry)
                                        }
                                    />
                                ) : null}
                            </ScheduleActionCell>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function WeeklyEntryDetails({
    entrySegments,
    parentSourceLabel,
    onEditEntry,
    onDeleteEntry,
}: {
    readonly entrySegments: ReadonlyArray<WeeklyAvailabilityEntrySegment>;
    readonly parentSourceLabel: SourceLabel;
    readonly onEditEntry: (entry: WeeklyAvailabilityEntry) => void;
    readonly onDeleteEntry: (entry: WeeklyAvailabilityEntry) => void;
}) {
    const showSourceLabels = entrySegments.some(
        (segment) => segment.sourceLabel !== parentSourceLabel,
    );

    return (
        <div className={SCHEDULE_DETAIL_CONTAINER_CLASS}>
            <div className="space-y-1">
                {entrySegments.map((segment) => {
                    return (
                        <div
                            key={segment.id}
                            className={cn(
                                getScheduleRowClass(false),
                                SCHEDULE_ROW_PADDING_CLASS,
                                'min-h-6 text-xs',
                            )}
                        >
                            <div />
                            <div className="flex justify-start">
                                <StatusBadge
                                    type={getAvailabilityType(segment.status)}
                                />
                            </div>
                            <div
                                className={cn(
                                    'text-foreground grid min-w-0 grid-cols-[5.5rem_minmax(0,1fr)] items-center gap-x-3 tabular-nums',
                                    !showSourceLabels ? 'col-span-2' : '',
                                )}
                            >
                                <span
                                    className="text-muted-foreground truncate"
                                    title={segment.dayLabel}
                                >
                                    {segment.dayLabel}
                                </span>
                                <span className="whitespace-nowrap">
                                    {segment.timeLabel}
                                </span>
                            </div>
                            {showSourceLabels ? (
                                <ScheduleSourceCell
                                    sourceLabel={segment.sourceLabel}
                                />
                            ) : null}
                            <ScheduleActionCell>
                                {segment.source === 'manual' ? (
                                    <AvailabilityActionMenu
                                        onEdit={() =>
                                            onEditEntry(segment.entry)
                                        }
                                        onDelete={() =>
                                            onDeleteEntry(segment.entry)
                                        }
                                    />
                                ) : null}
                            </ScheduleActionCell>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function TimeboxedScheduleRow({
    item,
    isExpanded,
    onToggleExpanded,
    onEditEntry,
    onDeleteEntry,
}: {
    readonly item: TimeboxedAvailabilitySegment;
    readonly isExpanded: boolean;
    readonly onToggleExpanded: () => void;
    readonly onEditEntry: (entry: TimeboxedAvailabilityEntry) => void;
    readonly onDeleteEntry: (entry: TimeboxedAvailabilityEntry) => void;
}) {
    const isExpandable = item.entries.length > 1;
    const singleEntry = item.entries.length === 1 ? item.entries[0] : null;
    const editableSingleEntry =
        !isExpandable && singleEntry?.source === 'manual' ? singleEntry : null;

    return (
        <Fragment>
            <div
                className={cn(
                    getScheduleRowClass(item.hasConflict),
                    SCHEDULE_ROW_PADDING_CLASS,
                )}
            >
                <div className="flex justify-start">
                    {isExpandable ? (
                        <EntriesDisclosureButton
                            isExpanded={isExpanded}
                            onToggleExpanded={onToggleExpanded}
                        />
                    ) : null}
                </div>
                <div className="flex justify-start">
                    <StatusBadge type={item.type} />
                </div>
                <div className="text-xs whitespace-nowrap tabular-nums">
                    {item.timeLabel}
                </div>
                <ScheduleSourceCell
                    sourceLabel={item.sourceLabel}
                    hasConflict={item.hasConflict}
                />
                <ScheduleActionCell>
                    {editableSingleEntry ? (
                        <AvailabilityActionMenu
                            onEdit={() => onEditEntry(editableSingleEntry)}
                            onDelete={() => onDeleteEntry(editableSingleEntry)}
                        />
                    ) : null}
                </ScheduleActionCell>
            </div>
            {isExpandable && isExpanded ? (
                <TimeboxedEntryDetails
                    entrySegments={item.entrySegments}
                    onEditEntry={onEditEntry}
                    onDeleteEntry={onDeleteEntry}
                />
            ) : null}
        </Fragment>
    );
}

function ShiftScheduleRow({
    item,
}: {
    readonly item: Extract<
        TimeboxedScheduleSegment,
        { readonly kind: 'shift' }
    >;
}) {
    return (
        <div
            className={cn(
                getScheduleRowClass(false),
                SCHEDULE_ROW_PADDING_CLASS,
            )}
        >
            <div />
            <div className="flex justify-start">
                <StatusBadge type="shift" />
            </div>
            <div className="text-xs whitespace-nowrap tabular-nums">
                {item.timeLabel}
            </div>
            <ScheduleSourceCell sourceLabel={item.details} />
            <ScheduleActionCell />
        </div>
    );
}

function WeeklyScheduleListRow({
    item,
    isExpanded,
    onToggleExpanded,
    onEditEntry,
    onDeleteEntry,
}: {
    readonly item: WeeklyScheduleRow;
    readonly isExpanded: boolean;
    readonly onToggleExpanded: () => void;
    readonly onEditEntry: (entry: WeeklyAvailabilityEntry) => void;
    readonly onDeleteEntry: (entry: WeeklyAvailabilityEntry) => void;
}) {
    const isExpandable = item.entries.length > 1;
    const singleEntry = item.entries.length === 1 ? item.entries[0] : null;
    const editableSingleEntry =
        !isExpandable && singleEntry?.source === 'manual' ? singleEntry : null;

    return (
        <Fragment>
            <div
                className={cn(
                    getScheduleRowClass(item.hasConflict),
                    SCHEDULE_ROW_PADDING_CLASS,
                )}
            >
                <div className="flex justify-start">
                    {isExpandable ? (
                        <EntriesDisclosureButton
                            isExpanded={isExpanded}
                            onToggleExpanded={onToggleExpanded}
                        />
                    ) : null}
                </div>
                <div className="flex justify-start">
                    <StatusBadge type={getAvailabilityType(item.status)} />
                </div>
                <div className="text-xs whitespace-nowrap tabular-nums">
                    {item.timeLabel}
                </div>
                <ScheduleSourceCell
                    sourceLabel={item.sourceLabel}
                    hasConflict={item.hasConflict}
                />
                <ScheduleActionCell>
                    {editableSingleEntry ? (
                        <AvailabilityActionMenu
                            onEdit={() => onEditEntry(editableSingleEntry)}
                            onDelete={() => onDeleteEntry(editableSingleEntry)}
                        />
                    ) : null}
                </ScheduleActionCell>
            </div>
            {isExpandable && isExpanded ? (
                <WeeklyEntryDetails
                    entrySegments={item.entrySegments}
                    parentSourceLabel={item.sourceLabel}
                    onEditEntry={onEditEntry}
                    onDeleteEntry={onDeleteEntry}
                />
            ) : null}
        </Fragment>
    );
}
